import { assert } from './assert';

// Same thresholds as CreditsUsageBar.tsx in the chat frontend (monorepo.vibedit, web/src/Chat/
// components/MessageInput/ChatSettingsPopover).
// FIXME(credits-usage-denominator): the chat bar divides by the unexpired notes' original amount;
// this one divides by what every note issued since the usage window opened made spendable (see
// `computeCreditsUsage`). Acceptable for now: the chat bar drops spend from short-lived notes
// that already expired, which is what this page set out to show. The two surfaces disagree for
// one balance until the chat frontend adopts the same `spendable` (suggested to its owner along
// with PR #901); then both should share one definition of used and one threshold source.
const WARNING_PERCENT = 65;
const CRITICAL_PERCENT = 90;

export type CreditsUsageSeverity = 'normal' | 'warning' | 'critical';

export type CreditsUsage = {
  used: number;
  /**
   * The bar's empty part: `spendable − used`. Equals the balance only while the balance is within
   * [0, spendable]; show `creditsLeft` when the figure is meant as "what you have left".
   */
  unfilled: number;
  /** used / spendable as a whole percentage, rounded down; the number the thresholds compare. */
  percent: number;
  severity: CreditsUsageSeverity;
};

/**
 * How much of the credits made spendable (granted minus expired unused) the balance has consumed.
 * The balance can sit below zero (down to `MINIMUM_CREDITS_ALLOWED`) or above `spendable`, so
 * `used` is clamped to [0, spendable]: the bar shows at most a full pool.
 */
export function computeCreditsUsage(spendable: number, creditsLeft: number): CreditsUsage {
  assert(spendable > 0, `credits usage needs positive spendable credits, got ${spendable}`);
  const used = Math.min(spendable, Math.max(0, spendable - creditsLeft));
  // Rounded once, then both displayed and compared: "65%" is exactly where the colour changes.
  const percent = Math.floor((used / spendable) * 100);
  const severity: CreditsUsageSeverity =
    percent >= CRITICAL_PERCENT ? 'critical' : percent >= WARNING_PERCENT ? 'warning' : 'normal';
  return { used, unfilled: spendable - used, percent, severity };
}
